import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export type SortValue = string | number | undefined;

export type SortAccessors<T> = Record<string, (item: T) => SortValue>;

export type OpenXAgentTableSorting = {
  orderBy: string | null;
  direction: SortDirection;
  onSort: (columnId: string) => void;
};

export const getSortDirection = (sorting: OpenXAgentTableSorting, columnId: string): SortDirection | false =>
  sorting.orderBy === columnId ? sorting.direction : false;

const COMPACT_SUFFIXES: Record<string, number> = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 };

// matches formatted metrics like `$48.4K`, `11.6M/mo`, `89%` or `1,250`
const COMPACT_NUMBER = /^[$€£]?\s*(-?\d[\d,]*(?:\.\d+)?)\s*([KMBT])?\s*(?:%|\/\w+)?$/i;

export const parseCompactNumber = (value: string): number | undefined => {
  const match = COMPACT_NUMBER.exec(value.trim());
  if (!match) return undefined;

  const [, digits = '', suffix] = match;
  return Number.parseFloat(digits.replaceAll(',', '')) * (suffix ? (COMPACT_SUFFIXES[suffix.toUpperCase()] ?? 1) : 1);
};

const toComparable = (value: SortValue) => (typeof value === 'string' ? (parseCompactNumber(value) ?? value) : value);

export const compareSortValues = (a: SortValue, b: SortValue, direction: SortDirection) => {
  const left = toComparable(a);
  const right = toComparable(b);

  if (left === undefined || left === '') return right === undefined || right === '' ? 0 : 1;
  if (right === undefined || right === '') return -1;

  const result =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });

  return direction === 'asc' ? result : -result;
};

export const useSorting = <T>(items: T[], accessors: SortAccessors<T>) => {
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [direction, setDirection] = useState<SortDirection>('asc');

  const onSort = useCallback(
    (columnId: string) => {
      if (orderBy !== columnId) {
        setOrderBy(columnId);
        setDirection('asc');
      } else if (direction === 'asc') {
        setDirection('desc');
      } else {
        setOrderBy(null);
        setDirection('asc');
      }
    },
    [orderBy, direction]
  );

  const accessor = orderBy ? accessors[orderBy] : undefined;

  const sortedItems = useMemo(
    () => (accessor ? [...items].sort((a, b) => compareSortValues(accessor(a), accessor(b), direction)) : items),
    [items, accessor, direction]
  );

  const sorting: OpenXAgentTableSorting = useMemo(() => ({ orderBy, direction, onSort }), [orderBy, direction, onSort]);

  return { sortedItems, sorting };
};
